import React, { useState, useEffect } from 'react';
import { HelpCircle, X } from 'lucide-react';

// Persistent in-app instructions for the Custom pages banner system.
// Click reveals a modal walking through the architecture + how to add
// a new custom page. Mirrors the BannerSizingInfo pattern used elsewhere
// in this tab so the visual + interaction is familiar.
export default function CustomPagesHelp({ style }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'transparent', border: 'none', padding: 0,
          color: 'var(--navy, #1e3a8a)', fontSize: 12.5, cursor: 'pointer',
          textDecoration: 'underline', textUnderlineOffset: 2,
          ...style,
        }}
      >
        <HelpCircle size={14} />
        How this works (+ adding pages)
      </button>

      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(10, 15, 25, 0.82)',
            zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 32,
          }}
        >
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close"
            style={{
              position: 'absolute', top: 16, right: 16,
              background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff',
              borderRadius: 4, padding: 6, cursor: 'pointer', display: 'inline-flex',
            }}
          >
            <X size={18} />
          </button>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 10, maxWidth: 760, width: '100%',
              maxHeight: '90vh', overflow: 'auto', padding: 28,
              boxShadow: '0 20px 60px rgba(0,0,0,0.4)', fontSize: 13.5, lineHeight: 1.55,
            }}
          >
            <h2 style={{ margin: '0 0 6px', color: 'var(--navy)', fontSize: 22 }}>
              Custom pages — how this works
            </h2>
            <p style={{ margin: '0 0 18px', color: 'var(--gray-600)' }}>
              How banner overrides flow from this admin to each storefront page, and what it takes to add another.
            </p>

            <h3 style={h3}>What it does</h3>
            <p style={p}>
              Each row above lets you save a <strong>title override</strong>, <strong>subhead override</strong>,
              and <strong>banner image</strong> for one storefront page that isn't a BC category
              (so it doesn't fit in the categories table below). When you upload an image, the
              storefront page renders a hero band — same visual treatment as the PDP banner —
              with the image cropped via <code>object-fit: cover</code>, a 0.15→0.40 dark gradient
              overlay for legibility, and your title + subhead in white over the top.
            </p>
            <p style={p}>
              Leave a row blank and the storefront page renders its default treatment (no banner band).
              Hard refresh the storefront after saving to bust the 30s edge cache.
            </p>

            <h3 style={h3}>Architecture (5 layers)</h3>
            <ol style={ol}>
              <li>
                <strong>Storage</strong> — Neon table <code>cp_page_banners</code>: <code>slug</code> primary
                key, plus <code>title</code> / <code>subhead</code> / <code>image_url</code> / <code>enabled</code>.
                One row per page.
              </li>
              <li>
                <strong>Public read API</strong> — <code>/.netlify/functions/page-banner?slug=&lt;slug&gt;</code>.
                Returns <code>{`{ enabled, title, subhead, image_url }`}</code> or <code>{`{ enabled: false }`}</code>.
                Edge-cached 30s.
              </li>
              <li>
                <strong>Storefront partial</strong> —{' '}
                <code>templates/components/atf/common/page-banner.html</code>. A reusable shell + IIFE
                that derives the slug from <code>window.location.pathname</code>, fetches the API,
                and unhides itself only when <code>image_url</code> is saved.
              </li>
              <li>
                <strong>Shared SCSS</strong> —{' '}
                <code>assets/scss/atf/common/page-banner.scss</code>, imported by{' '}
                <code>atf-page.scss</code> (which every custom-template page already loads).
              </li>
              <li>
                <strong>Admin row</strong> — entry in <code>CUSTOM_PAGES</code> in{' '}
                <code>src/pages/BigCommerce/CustomPagesSection.js</code> (this page).
              </li>
            </ol>

            <h3 style={h3}>To add another custom page (3 lines of code)</h3>
            <ol style={ol}>
              <li>
                Append an entry to <code>CUSTOM_PAGES</code>:
                <pre style={pre}>{`{ slug: 'our-guarantee', label: 'Our Guarantee', url: '/our-guarantee/' },`}</pre>
              </li>
              <li>
                Drop the partial into that page's Stencil template (e.g.{' '}
                <code>templates/pages/custom/page/atf-our-guarantee.html</code>) inside the{' '}
                <code>{`{{#partial "page"}}`}</code> block, before the page's main content:
                <pre style={pre}>{`{{> components/atf/common/page-banner}}`}</pre>
              </li>
              <li>
                Deploy the theme (<code>dotnet run -- deploy</code> from{' '}
                <code>SyncStoresSimplifiedCA</code>) and redeploy cpx-prototype on push.
              </li>
            </ol>

            <h3 style={h3}>Constraints + edge cases</h3>
            <ul style={ol}>
              <li>
                <strong>Single-segment slugs only</strong>. The partial bails out on multi-segment
                paths like <code>/account/orders</code>. For those, swap the URL-derivation in
                the partial for an explicit data attribute.
              </li>
              <li>
                <strong>Image required</strong>. Title or subhead alone won't render a banner —
                the IIFE only unhides when <code>image_url</code> is set. If you want title-only
                hero bands, extend the partial to render a solid-color band when image is missing.
              </li>
              <li>
                <strong>Pages on the default <code>page.html</code> template don't qualify</strong> by
                default (Privacy Policy, Terms of Service, FAQs, Our Guarantee). Either give them a
                custom template, or add a one-off banner shell to <code>page.html</code> guarded on
                a slug allow-list.
              </li>
              <li>
                <strong>Direct upload uses /r2-upload</strong>, which writes under the{' '}
                <code>marketing/</code> prefix in R2. The <code>r2-image</code> proxy already
                allow-lists that prefix.
              </li>
            </ul>

            <h3 style={h3}>Default banner sizing</h3>
            <p style={p}>
              Banner band is ~200px tall on desktop, ~140px on mobile. Image is{' '}
              <code>object-fit: cover</code>, so design wide (target 2560×400 or wider).
              Center the subject in the middle <code>1400×120</code> safe zone — edges will be cropped
              on different viewport widths.
            </p>
          </div>
        </div>
      )}
    </>
  );
}

const h3 = { margin: '18px 0 6px', fontSize: 14, color: 'var(--navy)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 };
const p = { margin: '0 0 8px' };
const ol = { margin: '0 0 8px', paddingLeft: 22 };
const pre = { background: '#0f172a', color: '#e2e8f0', padding: '10px 12px', borderRadius: 6, fontSize: 12, margin: '6px 0', overflowX: 'auto' };
